// Pure parser that extracts interview-prep-relevant fields from a match's
// ai_analysis. Defensive against string-vs-object payloads (the old
// InterviewPrep component had the same guard).
//
// The questions themselves are produced server-side by the Supabase-dashboard
// prompt, which writes them under ai_analysis.analysis.questions as a flat
// object { question1, question2, ..., question10 }. This helper only reads.

import { explanationText } from "@/utils/matches/analysisText";

export interface InterviewPrepCategory {
	name: string;
	score: number; // 0–10 scale, consistent with MatchCircularOverview
	explanation: string;
}

export interface InterviewPrepQuestion {
	key: string; // e.g. "question1"
	index: number; // 1..10
	text: string;
}

export interface InterviewPrepData {
	overallScore: number | null;
	recommendation: string | null;
	strengths: string[];
	gaps: string[];
	categories: InterviewPrepCategory[];
	questions: InterviewPrepQuestion[];
	hasAnalysis: boolean;
}

const parseAiAnalysis = (raw: unknown): Record<string, unknown> | null => {
	if (!raw) return null;
	if (typeof raw === "string") {
		try {
			return JSON.parse(raw) as Record<string, unknown>;
		} catch {
			return null;
		}
	}
	if (typeof raw === "object") return raw as Record<string, unknown>;
	return null;
};

const extractQuestions = (questionsRaw: unknown): InterviewPrepQuestion[] => {
	if (!questionsRaw || typeof questionsRaw !== "object") return [];
	const result: InterviewPrepQuestion[] = [];
	for (const [key, value] of Object.entries(questionsRaw as Record<string, unknown>)) {
		if (typeof value !== "string" || !value.trim()) continue;
		const match = key.match(/^question(\d+)$/i);
		const index = match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
		result.push({ key, index, text: value });
	}
	// Numeric sort so question10 lands after question9 instead of after question1.
	result.sort((a, b) => a.index - b.index);
	return result;
};

const extractCategories = (matchingAnalysis: unknown): InterviewPrepCategory[] => {
	if (!matchingAnalysis || typeof matchingAnalysis !== "object") return [];
	const result: InterviewPrepCategory[] = [];
	for (const [name, raw] of Object.entries(matchingAnalysis as Record<string, unknown>)) {
		if (!raw || typeof raw !== "object") continue;
		const entry = raw as Record<string, unknown>;
		const ratio = typeof entry["Match Ratio"] === "number" ? entry["Match Ratio"] : null;
		const score = ratio !== null ? Math.max(0, Math.min(10, ratio * 10)) : 0;
		const explanation = explanationText(entry["Explanation"]);
		result.push({ name, score, explanation });
	}
	return result;
};

// When a snapshot is provided (frozen question list persisted in
// match_interview_prep.notes.questions_snapshot), it overrides whatever is
// currently in ai_analysis.analysis.questions. Only the question list is
// frozen — all other context fields still come from the live ai_analysis.
export const readInterviewPrep = (
	match: { ai_analysis?: unknown } | null,
	snapshot?: InterviewPrepQuestion[] | null
): InterviewPrepData => {
	const empty: InterviewPrepData = {
		overallScore: null,
		recommendation: null,
		strengths: [],
		gaps: [],
		categories: [],
		questions: [],
		hasAnalysis: false,
	};

	if (!match) return empty;

	const aiAnalysis = parseAiAnalysis(match.ai_analysis);
	if (!aiAnalysis) return empty;

	const analysisRaw = aiAnalysis.analysis ?? aiAnalysis;
	if (!analysisRaw || typeof analysisRaw !== "object") return empty;
	const analysis = analysisRaw as Record<string, unknown>;

	const overallScore =
		typeof analysis.overall_compatibility_score === "number"
			? analysis.overall_compatibility_score
			: null;

	const recommendation =
		typeof analysis.final_recommendation === "string" && analysis.final_recommendation.trim()
			? analysis.final_recommendation
			: null;

	const strengths = Array.isArray(analysis.key_strengths)
		? analysis.key_strengths.filter((s: unknown): s is string => typeof s === "string" && !!s.trim())
		: [];

	const gaps = Array.isArray(analysis.key_gaps)
		? analysis.key_gaps.filter((g: unknown): g is string => typeof g === "string" && !!g.trim())
		: [];

	const categories = extractCategories(analysis.matching_analysis);
	const questions =
		snapshot && snapshot.length > 0
			? [...snapshot].sort((a, b) => a.index - b.index)
			: extractQuestions(analysis.questions);

	return {
		overallScore,
		recommendation,
		strengths,
		gaps,
		categories,
		questions,
		hasAnalysis: true,
	};
};
